import {
  Database,
  Home,
  LayoutDashboard,
  LineChart,
  Ruler,
  Settings,
  type LucideIcon,
} from 'lucide-react';

/**
 * The product's information architecture, in one list.
 *
 * What changed and why:
 *
 * - Four surfaces used to overlap. "Explorer" listed charts *and* dashboards
 *   with a preview; "Charts" (internally "Reports") listed charts with a preview;
 *   "Dashboards" listed dashboards with a preview. Three list-detail screens for
 *   two kinds of thing. They are now two libraries — Charts and Dashboards —
 *   each with the union of what those screens could do, and cross-cutting search
 *   moved to the command palette (⌘K), which is where a user looking for "that
 *   revenue thing" actually starts.
 * - The builder was a hidden mode of the shell with no address. It is a route now
 *   (`/charts/:id/edit`), so it can be linked, reloaded and left with the browser
 *   back button.
 * - Two nav items ("AI Insights", "Alerts") pointed at screens with no backend.
 *   They were demoted to a "Labs" group and are now gone entirely, along with
 *   their routes and pages: the navigation lists what the product does.
 */
export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Which counter to show, if any. */
  count?: 'charts' | 'dashboards' | 'sources';
  /** Route prefixes that should light this item up. */
  match?: string[];
  description: string;
}

export const PRIMARY_NAV: NavItem[] = [
  {
    to: '/',
    label: 'Home',
    icon: Home,
    description: 'What changed, what needs attention, and where to pick up.',
  },
  {
    to: '/charts',
    label: 'Charts',
    icon: LineChart,
    count: 'charts',
    match: ['/charts'],
    description: 'Every saved chart — search, preview, edit, share.',
  },
  {
    to: '/dashboards',
    label: 'Dashboards',
    icon: LayoutDashboard,
    count: 'dashboards',
    match: ['/dashboards'],
    description: 'Boards built by hand or by the assistant.',
  },
  {
    to: '/data',
    label: 'Data',
    icon: Database,
    count: 'sources',
    match: ['/data'],
    description: 'Connected APIs, tables and webhook streams.',
  },
  {
    // The semantic layer had no address. Every definition a build reasons over
    // was reachable only through the source that happened to own it.
    to: '/metrics',
    label: 'Metrics',
    icon: Ruler,
    match: ['/metrics'],
    description: 'What each measure means, who uses it, and the words for it.',
  },
];

export const SETTINGS_NAV: NavItem = {
  to: '/settings',
  label: 'Settings',
  icon: Settings,
  match: ['/settings'],
  description: 'Workspace defaults, appearance and about.',
};

/** Is `pathname` inside this nav item? */
export function isNavActive(item: NavItem, pathname: string): boolean {
  if (item.to === '/') return pathname === '/';
  const prefixes = item.match ?? [item.to];
  return prefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/**
 * The breadcrumb for a path.
 *
 * Kept derivable from the URL rather than pushed up from each screen: a
 * breadcrumb that a page has to remember to set is a breadcrumb that goes stale.
 */
export interface Crumb {
  label: string;
  to?: string;
}

export function crumbsFor(pathname: string, names: { chart?: string; dashboard?: string }): Crumb[] {
  const parts = pathname.split('/').filter(Boolean);
  if (!parts.length) return [{ label: 'Home' }];

  const [root, id, action] = parts;

  if (root === 'charts') {
    const crumbs: Crumb[] = [{ label: 'Charts', to: '/charts' }];
    if (id === 'new') return [...crumbs, { label: 'New chart' }];
    if (id) crumbs.push({ label: names.chart || 'Chart', to: `/charts/${id}` });
    if (action === 'edit') crumbs.push({ label: 'Edit' });
    return crumbs;
  }
  if (root === 'dashboards') {
    const crumbs: Crumb[] = [{ label: 'Dashboards', to: '/dashboards' }];
    if (id) crumbs.push({ label: names.dashboard || 'Dashboard' });
    return crumbs;
  }
  if (root === 'data') return [{ label: 'Data' }];
  if (root === 'metrics') return [{ label: 'Metrics' }];
  if (root === 'settings') return [{ label: 'Settings' }];
  return [{ label: 'Home' }];
}
